"use client";

/**
 * Standalone "add to chat context" button.
 *
 * Encapsulates the `useAddToContext()` behaviour behind a single reusable
 * `MessageSquareShare` icon button so it can be dropped anywhere a node is
 * in hand — the generic `ModelDetailView` header (via the framework's
 * `setAddToContextButton` injection), `InPlaceEditable`'s `onChat` slot, or
 * a bespoke entity layout. Clicking it queues the node as pending chat
 * context (the chat input inserts `@<name>` at the cursor) and opens /
 * creates a chat.
 *
 * Accepts a loaded node and derives the context item from its standard
 * fields. RFLP nodes have no `search_path`, so it falls back to `name` —
 * which is exactly what the RFLP @-picker matches on
 * (`search-rflp-context-items-*`).
 */

import React from "react";
import { MessageSquareShare } from "lucide-react";
import { Button } from "@sourcepilot/ui/framework/ui/atoms/button";
import { useAddToContext } from "@/hooks/use-add-to-context";

export interface AddToContextButtonProps {
  /** The loaded node (needs `id`; uses `name` / `search_path` / `_label`). */
  node: any;
  /** Optional model name — accepted so it matches the framework's
   *  injected-button signature; not otherwise needed. */
  model?: string;
  className?: string;
}

export function AddToContextButton({ node, className }: AddToContextButtonProps) {
  const { addToContext } = useAddToContext();
  if (!node?.id) return null;
  return (
    <Button
      variant="ghost"
      size="sm"
      className={className ?? "p-0 text-black"}
      title="Add to chat context"
      onClick={(e) => {
        e.stopPropagation();
        addToContext({
          id: node.id,
          name: node.name || "",
          search_path: node.search_path || node.name || "",
          _label: node._label,
        });
      }}
    >
      <MessageSquareShare className="h-5 w-5" />
    </Button>
  );
}
